import React, { useState } from "react";
import { toast } from "sonner";
import { Loader2, Mail, Paperclip, CheckCircle2 } from "lucide-react";
import InfoLayout from "@/components/InfoLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import api, { errMessage } from "@/lib/api";

const CATEGORIES = [
  "Account / Login", "QR Code", "Reference Collection", "Download / Export",
  "Sharing / Public Page", "Bug / Technical Issue", "Report a Problem",
  "Feature Request", "Billing", "Other",
];

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export default function Support() {
  const [form, setForm] = useState({ name: "", email: "", category: "", subject: "", description: "", projectLink: "" });
  const [file, setFile] = useState(null);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setErrors((e) => ({ ...e, [k]: undefined })); };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Name is required.";
    if (!form.email.trim()) e.email = "Email is required.";
    else if (!EMAIL_RE.test(form.email.trim())) e.email = "Enter a valid email address.";
    if (!form.category) e.category = "Please select a category.";
    if (!form.subject.trim()) e.subject = "Subject is required.";
    if (!form.description.trim()) e.description = "Description is required.";
    if (file && file.size > 5 * 1024 * 1024) e.file = "Attachment must be 5 MB or smaller.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (loading) return; // prevent duplicate submissions while processing
    if (!validate()) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name.trim());
      fd.append("email", form.email.trim());
      fd.append("category", form.category);
      fd.append("subject", form.subject.trim());
      fd.append("description", form.description.trim());
      fd.append("project_link", form.projectLink.trim());
      if (file) fd.append("attachment", file);
      await api.post("/support", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSent(true);
      setForm({ name: "", email: "", category: "", subject: "", description: "", projectLink: "" });
      setFile(null);
    } catch (err) {
      toast.error(errMessage(err) || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <InfoLayout
      title="How can we help?"
      docTitle="Support"
      subtitle="Need assistance with CiteQR? Whether you're having trouble with your account, QR code, reference collection, downloads, or another feature, we're here to help."
    >
      <Card className="mb-6 border-slate-200 bg-slate-50 shadow-sm" data-testid="support-contact-card">
        <CardContent className="flex items-start gap-3 p-5">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-white"><Mail className="h-5 w-5 text-[#0D9488]" /></div>
          <div>
            <h2 className="text-base font-semibold text-slate-900">Contact Support</h2>
            <p className="mt-1 text-sm text-slate-600">
              Email: <a href="mailto:support@getciteqr.com" className="font-medium text-[#0D9488] hover:underline" data-testid="support-email-link">support@getciteqr.com</a>
            </p>
            <p className="text-sm text-slate-500">We typically respond within 1–2 business days.</p>
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-6">
          {sent ? (
            <div className="py-6 text-center" data-testid="support-success">
              <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50"><CheckCircle2 className="h-6 w-6 text-emerald-600" /></div>
              <p className="text-base font-medium text-slate-900">Your support request has been received.</p>
              <p className="mt-1 text-sm text-slate-600">We'll get back to you within 1–2 business days.</p>
              <Button variant="outline" className="mt-4" onClick={() => setSent(false)} data-testid="support-another">Submit another request</Button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" noValidate>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="s-name">Name</Label>
                  <Input id="s-name" value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="support-name" />
                  {errors.name && <p className="text-xs text-red-600" data-testid="err-name">{errors.name}</p>}
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="s-email">Email associated with your CiteQR account</Label>
                  <Input id="s-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="support-email" />
                  {errors.email && <p className="text-xs text-red-600" data-testid="err-email">{errors.email}</p>}
                </div>
              </div>

              <div className="space-y-1.5">
                <Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => set("category", v)}>
                  <SelectTrigger data-testid="support-category"><SelectValue placeholder="Select a category" /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((c) => (
                      <SelectItem key={c} value={c} data-testid={`support-category-${c.replace(/[^a-z]+/gi, "-").toLowerCase()}`}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.category && <p className="text-xs text-red-600" data-testid="err-category">{errors.category}</p>}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="s-subject">Subject</Label>
                <Input id="s-subject" value={form.subject} onChange={(e) => set("subject", e.target.value)} data-testid="support-subject" />
                {errors.subject && <p className="text-xs text-red-600" data-testid="err-subject">{errors.subject}</p>}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="s-desc">Description</Label>
                <Textarea id="s-desc" rows={5} value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="support-description" />
                {errors.description && <p className="text-xs text-red-600" data-testid="err-description">{errors.description}</p>}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="s-link">CiteQR project / public-page link <span className="text-slate-400">(optional)</span></Label>
                <Input id="s-link" placeholder="https://getciteqr.com/r/..." value={form.projectLink} onChange={(e) => set("projectLink", e.target.value)} data-testid="support-link" />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="s-file">Screenshot / file <span className="text-slate-400">(optional, image or PDF, max 5 MB)</span></Label>
                <label htmlFor="s-file" className="flex cursor-pointer items-center gap-2 rounded-md border border-dashed border-slate-300 px-3 py-2.5 text-sm text-slate-600 hover:border-[#0D9488]">
                  <Paperclip className="h-4 w-4" />
                  <span data-testid="support-file-label">{file ? file.name : "Choose a file to attach"}</span>
                </label>
                <input id="s-file" type="file" accept="image/png,image/jpeg,image/gif,image/webp,application/pdf" className="hidden" onChange={(e) => { setFile(e.target.files?.[0] || null); setErrors((x) => ({ ...x, file: undefined })); }} data-testid="support-file" />
                {errors.file && <p className="text-xs text-red-600" data-testid="err-file">{errors.file}</p>}
              </div>

              <p className="text-xs leading-relaxed text-slate-500">
                Please do not include passwords, payment information, or other sensitive personal information.
              </p>

              <Button type="submit" disabled={loading} className="bg-[#0A2540] text-white hover:bg-[#0c2e4e]" data-testid="support-submit">
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Submit request
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </InfoLayout>
  );
}
